import {
  activateRateCard,
  deactivateRateCard,
  deleteRateCard,
  fetchRateCard,
} from "api/ratecard"
import { queryCache, useQuery } from "react-query"

import Badge from "components/Badge"
import BaseRate from "./BaseRate"
import Breadcrumbs from "components/Breadcrumbs"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import { ColorMap } from "utils/color"
import { Container } from "components/Page/Container"
import { ReactComponent as DeactivateIcon } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { Header } from "components/Page"
import MoreActions from "components/MoreActions"
import RateCardProfile from "./RateCardProfile"
import Surface from "components/Surface"
import { WithAuthority } from "components/WithAuthority"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useConfirmation } from "components/Confirmation"
import { useHistory } from "react-router"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSendFeedBack } from "components/FeedbackSnackbar"

export default function RateCardDetails() {
  const { t } = useI18n()
  const feedback = useSendFeedBack()
  const confirm = useConfirmation()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const { hasAuthority } = useAuthenticated()

  const { data } = useQuery(["rate-card-details", id], fetchRateCard)

  const activate = () =>
    confirm(
      {
        action: () => activateRateCard(id),
        onSuccess: () => {
          feedback(t("rate.card.activate.success"))
          queryCache.invalidateQueries(["rate-card-details"])
        },
        onError: (e) => {
          feedback(t(e.message) || t("rate.card.activate.error"), "error")
          queryCache.invalidateQueries(["rate-card-details"])
        },
      },
      {
        type: "primary",
        submitLabel: t("actions.activate"),
        cancelLabel: t("actions.cancel"),

        title: t("rate.card.modal.activate.title"),
        description: t("rate.card.modal.activate.description"),
      },
    )

  const deactivate = () =>
    confirm(
      {
        action: () => deactivateRateCard(id),
        onSuccess: () => {
          feedback(t("rate.card.deactivate.success"))
          queryCache.invalidateQueries(["rate-card-details"])
        },
        onError: (e) => {
          feedback(t(e.message) || t("rate.card.deactivate.error"), "error")
          queryCache.invalidateQueries(["rate-card-details"])
        },
      },
      {
        type: "primary_destructive",
        submitLabel: t("actions.deactivate"),
        cancelLabel: t("actions.cancel"),

        title: t("rate.card.modal.deactivate.title"),
        description: t("rate.card.modal.deactivate.description"),
      },
    )

  const _delete = () =>
    confirm(
      {
        action: () => deleteRateCard(id),
        onSuccess: () => {
          history.push(
            `/market-vendors/${data?.supplierId}/rate-cards/individual-spots`,
          )
          feedback(t("rate.card.delete.success"))
          queryCache.invalidateQueries(["rate-card-details"])
        },
        onError: (e) => {
          feedback(t(e.message) || t("rate.card.delete.error"), "error")
          queryCache.invalidateQueries(["rate-card-details"])
        },
      },
      {
        type: "primary_destructive",
        submitLabel: t("actions.delete"),
        cancelLabel: t("actions.cancel"),

        title: t("rate.card.modal.delete.title"),
        description: t("rate.card.modal.delete.description"),
      },
    )

  const canActivateOrDelete = data?.status !== "ACTIVE"

  return (
    <Container>
      <Breadcrumbs
        current={"RateCardDetails"}
        items={[
          {
            backLink: `${t("nav.admin")} / ${t("tabs.rate_card")}`,
            to: `/market-vendors/${data?.supplierId}/rate-cards/individual-spots`,
          },
        ]}
      />
      <Header
        label={data?.name}
        badge={
          <Badge color={ColorMap[data?.status || ""]}>{data?.status}</Badge>
        }
      >
        <WithAuthority
          allowed={["AUTH_EDIT_RATE_CARD", "AUTH_DELETE_RATE_CARD"]}
        >
          <ButtonGroup>
            <MoreActions
              options={
                canActivateOrDelete
                  ? [
                      // ** ACTIVATE
                      {
                        hidden: !hasAuthority(["AUTH_EDIT_RATE_CARD"]),
                        label: t("rate.card.activate.action"),
                        icon: <CheckIcon />,
                        onClick: activate,
                      },
                      // ** DELETE
                      {
                        hidden: !hasAuthority(["AUTH_DELETE_RATE_CARD"]),
                        label: t("rate.card.delete.action"),
                        icon: <DeleteIcon />,
                        onClick: _delete,
                      },
                    ]
                  : [
                      // ** DEACTIVATE
                      {
                        hidden: !hasAuthority(["AUTH_EDIT_RATE_CARD"]),
                        label: t("rate.card.deactivate.action"),
                        icon: <DeactivateIcon />,
                        onClick: deactivate,
                      },
                    ]
              }
            />
          </ButtonGroup>
        </WithAuthority>
      </Header>
      <Surface>
        {/* Details */}
        <section>
          <RateCardProfile data={data} />
        </section>

        {/* Base Rate */}
        <section className={styles.baseRateSection}>
          <header>
            <span className={styles.sectionTitle}>{t("base.rate")}</span>
          </header>
          <BaseRate rateCardId={id} />
        </section>
      </Surface>
    </Container>
  )
}

const styles = {
  sectionTitle: css`
    display: block;
    font-size: 14px;
    font-weight: bold;
  `,
  baseRateSection: css`
    margin-top: var(--spacing-xxl-6);
  `,
}
